// Date helpers for ISO `YYYY-MM-DD` strings used across the app.
// All Date construction is local-time on purpose: ISO strings here represent
// calendar days, never instants.

export type IsoDate = string;

const pad2 = (n: number): string => String(n).padStart(2, '0');

export const todayIso = (): IsoDate => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export type IsoDateParts = {
    year: number;
    month: number;
    day: number;
};

export const splitIsoDate = (iso: IsoDate): IsoDateParts => {
    const [year, month, day] = iso.split('-').map(Number);
    return { year, month, day };
};

export const isoToDate = (iso: IsoDate): Date => {
    const { year, month, day } = splitIsoDate(iso);
    return new Date(year, month - 1, day);
};

export const formatLongDay = (iso: IsoDate): string =>
    isoToDate(iso).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
    });

export const formatShortDate = (iso: IsoDate): string =>
    isoToDate(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });

export const formatJoinedDate = (iso: IsoDate): string => {
    const date = isoToDate(iso);
    if (Number.isNaN(date.getTime())) {
        return iso;
    }
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
};

export const formatMonthYear = (year: number, month: number): string =>
    new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

export type WeekdayHeader = {
    day: number;
    weekday: string;
};

export const weekdayHeader = (iso: IsoDate): WeekdayHeader => {
    const parts = splitIsoDate(iso);
    return {
        day: parts.day,
        weekday: new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
    };
};

export type MonthBounds = {
    monthStart: IsoDate;
    monthLast: IsoDate;
};

export const monthBounds = (year: number, month: number): MonthBounds => {
    const lastDay = new Date(year, month, 0).getDate();
    return {
        monthStart: `${year}-${pad2(month)}-01`,
        monthLast: `${year}-${pad2(month)}-${pad2(lastDay)}`,
    };
};

export const isInYearMonth = (iso: IsoDate, year: number, month: number): boolean => {
    const parts = splitIsoDate(iso);
    return parts.year === year && parts.month === month;
};
